/**
 * Game API Service
 *
 * Handles all API calls to the game endpoints.
 */

import type { EpisodeSaveState } from '../utils/sessionPersistence';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.selna.ai';

// ----------------------------------------------------------------------------
// Public catalog types (mirrors AdventureCatalogService on the backend)
// ----------------------------------------------------------------------------

export interface AdventureCharacterRef {
  id: string;
  name: string | null;
  role: string | null;
  avatarUrl: string | null;
  /** Small (~400px) JPEG preview, for catalog cast cards. Falls back to avatarUrl server-side if not yet backfilled. */
  avatarThumbUrl: string | null;
}

/**
 * Gameplay mode of an adventure.
 * - 'linear'      : linear narrative, one character per episode, fixed order, no choice.
 * - 'exploration' : player chooses who to talk to; each person tells their part of the
 *                   story, no mystery to solve.
 * - 'mystery'     : Agatha-Christie-style whodunnit; player chooses who to talk to and
 *                   cross-references clues gathered from multiple cast members.
 * - null          : legacy adventure created before the field was introduced (and before
 *                   the migration); the UI treats this as 'mystery' (the historical default).
 */
export type AdventureMode = 'linear' | 'exploration' | 'mystery' | null;

export interface AdventureSummary {
  id: string;
  title: string;
  /** URL slug for pretty detail-page URLs (`/adventure/a-setima-arca`). */
  slug: string;
  theme: string | null;
  tone: string | null;
  playerRole: string | null;
  totalEpisodes: number | null;
  heroImageUrl: string | null;
  premiseExcerpt: string;
  /**
   * Public back-cover blurb shown on the catalog card and the detail page.
   * Generated by the M3 pipeline step from the A3 outline (história) or
   * foundation+truth+investigation (whodunnit). null on adventures generated
   * before M3 was introduced — the backfill script populates those.
   *
   * The legacy `premise`/`premiseExcerpt` and `playerBriefing` fields are
   * intentionally NOT shown as marketing copy: premise pins character details
   * that read awkwardly out of context and can leak meta-instructions from the
   * raw prompt; briefing deliberately reveals the in-game framing and belongs
   * only to the first NPC.
   */
  synopsis: string | null;
  initialCharacter: AdventureCharacterRef | null;
  charactersPreview: AdventureCharacterRef[];
  mysteryMode: AdventureMode;
  /**
   * Número real de jogadores que chegaram ao ecrã "Aventura completa"
   * (playerStates com status 'completed' + playthroughHistory arquivada).
   * Ausente/undefined quando ainda não há conclusões — o badge não é renderizado.
   */
  completedCount?: number;
  /**
   * Frase do crítico IA, estilo contracapa/cartaz — só aspetos positivos.
   * Não há nota numérica: sem calibração entre aventuras o juiz não
   * discrimina (todas saem 8-9/10), por isso não é exposta publicamente.
   */
  criticQuote: string | null;
  /** Versão compacta (≤6 palavras) de `criticQuote`, para espaços pequenos como o card. */
  criticQuoteShort: string | null;
  /**
   * Featured flag: when true, the adventure is pinned to the top of the
   * public catalog. The front-end can render a "Em destaque" badge.
   */
  featured?: boolean;
}

export interface AdventureDetail extends AdventureSummary {
  /**
   * Back-office-generated Open Graph preview image (1200×630 JPEG: hero +
   * title + Selna logo). When present, social shares use this static URL
   * directly; when absent, the runtime `/og-image` endpoint composes one on
   * the fly as a fallback.
   */
  ogImageUrl?: string | null;
}

interface AdventuresListResponse {
  adventures: AdventureSummary[];
  total: number;
}

interface AdventureDetailResponse {
  adventure: AdventureDetail;
}

interface StartGameResponse {
  status: 'email_sent';
  message: string;
  isNewPlayer: boolean;
}

// Resposta do arranque anónimo (B2). Mesma forma do antigo /start-instant,
// mas sem isNewPlayer/email — o jogador ainda não tem email.
interface StartAnonymousResponse {
  status: 'ready';
  token: string;
  adventureId: string;
  adventureTitle: string;
  currentEpisode: number;
  totalEpisodes: number;
  playerStatus: 'active' | 'completed';
}

// Resposta do /resume (B5) — banner server-backed. `token` só vem quando o
// servidor renovou o token expirado.
export interface ResumeResponse {
  status: 'active' | 'completed' | 'not_found';
  adventureId?: string;
  adventureTitle?: string;
  currentEpisode?: number;
  totalEpisodes?: number;
  playerStatus?: 'active' | 'completed';
  hasEmail?: boolean;
  token?: string;
}

// Resposta do /attach-email (B3). `attached` = email anexado sem conflito;
// `conflict` = já existe run com este email — o jogador tem de escolher.
interface AttachEmailResponse {
  status: 'attached' | 'conflict';
  existing?: {
    adventureTitle: string;
    currentEpisode: number;
    totalEpisodes: number;
    playerStatus: 'active' | 'completed';
  };
  current?: {
    adventureTitle: string;
    currentEpisode: number;
    totalEpisodes: number;
  };
}

// Resposta do /attach-email/resolve-conflict (B4). `attached` = o run
// anónimo venceu (keep:'current'); `switch` = mantém-se o run do email
// (keep:'existing') e o cliente recebe um token novo para navegar para ele.
interface ResolveEmailConflictResponse {
  status: 'attached' | 'switch';
  token?: string;
  adventureId?: string;
  adventureTitle?: string;
  currentEpisode?: number;
  totalEpisodes?: number;
  playerStatus?: 'active' | 'completed';
}

/** One page of the spoiler-free graphic-novel intro shown before episode 1. */
export interface StoryboardPage {
  text: string;
  imageUrl: string;
}

interface PlayResponse {
  status: 'ready' | 'waiting' | 'completed';
  playerState: PlayerState;
  scenarioData?: ScenarioData;
  selectedCandidateId?: string;
  allowResolveEarly: boolean;
  adventureTitle: string;
  totalEpisodes: number;
  playerRole: string;
  estimatedReadyAt?: string;
  savedEpisodeState?: EpisodeSaveState | null;
  initialCharacterAvatarUrl?: string | null;
  /** Present only before episode 1, once all 4 intro images are generated. */
  openingStoryboard?: StoryboardPage[] | null;
}

interface EpisodeCompleteResponse {
  status: 'processing';
  episodeSummary: EpisodeSummary;
  waitingToken: string;
  isLastEpisode?: boolean;
  isResolveEpisodeCompleted?: boolean;
  totalEpisodes?: number;
  currentEpisode?: number;
}

interface WaitingResponse {
  nextEpisodeStatus:
    | 'pending'
    | 'processing_recap'
    | 'preparing_candidates'
    | 'candidates_ready'
    | 'preparing_selected_episode'
    | 'episode_ready'
    // Legacy states kept for backwards compatibility during rollout
    | 'generating'
    | 'ready';
  nextEpisodeMode?: 'standard' | 'resolve';
  scenarioReady?: boolean;
  /** HISTÓRIA bridge — written early in _advanceLinearPlayback (~5-15s after
   *  episode end). Frontend locks this once shown so a later re-generation inside
   *  `scenarioData.frontendSituation` can never replace it. */
  nextBridgeText?: string | null;
  /** Bridge text into the next encounter, appended to the recap so the two read as one chapter.
   *  Whodunnit only: available once the next episode finished preparing (episode_ready).
   *  História ignores this and uses nextBridgeText instead. */
  nextFrontendSituation?: string | null;
  lastEpisode?: {
    characterName: string;
    summary: EpisodeSummary;
    npcReaction: NPCReaction;
  };
  pendingReaction?: {
    characterName: string;
    npcReaction: NPCReaction;
  } | null;
  nextCandidates?: NextEpisodeCandidate[];
  selectedCandidateId?: string | null;
  allowResolveEarly: boolean;
  estimatedReadyAt?: string;
  adventureTitle: string;
  currentEpisode: number;
  totalEpisodes: number;
  isLastEpisode: boolean;
  // When adventure is completed
  status?: 'completed';
  initialCharacterAvatarUrl?: string | null;
  completedEpisodesForDots?: Array<{
    episodeNumber: number;
    characterName: string;
    avatarUrl: string | null;
  }>;
}

// Per-episode score breakdown
export interface EpisodeScore {
  episodeNumber: number;
  communication: number;
  contextAdaptation: number;
  goalAchievement: number;
  emotionalManagement: number;
  notes?: string;
}

// Score breakdown for final evaluation
// NOTE: Scores are calculated independently of NPC reactions
export interface ScoreBreakdown {
  finalScore: number;
  communicationScore: number;
  contextAdaptationScore: number;
  goalAchievementScore: number;
  emotionalManagementScore: number;
  episodeScores?: EpisodeScore[];
  episodeCount: number;
}

// Written evaluation from LLM
export interface WrittenEvaluation {
  overallAssessment: string;
  strengths: string[];
  areasToImprove: string[];
  communicationStyle: string;
  memorableMoment: string;
  adviceForNext: string;
}

// Final evaluation for completed adventure
export interface FinalEvaluation {
  score: number;
  breakdown: ScoreBreakdown;
  evaluation: WrittenEvaluation;
  generatedAt: string;
  error?: string;
}

// Completion response
interface CompletionResponse {
  status: 'completed';
  adventureId: string;
  adventureTitle: string;
  episodesCompleted: number;
  finalEvaluation: FinalEvaluation | null;
  completedAt: string;
  hasSubmittedHighscore: boolean;
  playerState: PlayerState;
}

// Highscore entry
export interface Highscore {
  id: string;
  playerName: string;
  score: number;
  odysseyId: string;
  adventureId: string;
  adventureTitle: string;
  episodesCompleted: number;
  completedAt: string;
  averageCommunicationScore: number;
  averageContextAdaptationScore: number;
  averageGoalAchievementScore: number;
  averageEmotionalManagementScore: number;
  submittedAt: string;
  rank?: number;
}

// Highscore submit response
interface HighscoreSubmitResponse {
  highscore: Highscore;
  message: string;
}

// Highscores list response
interface HighscoresListResponse {
  highscores: Highscore[];
  adventures: Array<{ id: string; title: string }>;
  total: number;
}

// Types
export interface PlayerState {
  id: string;
  adventureId: string;
  email: string;
  playerName?: string;
  currentPhase: string;
  currentEpisode: number;
  status: 'active' | 'completed' | 'abandoned';
  completedEpisodes: CompletedEpisode[];
  nextEpisode: {
    status: string;
    characterId?: string;
  };
}

export interface CompletedEpisode {
  episodeNumber: number;
  characterName: string;
  episodeSummary: EpisodeSummary;
  completedAt: string;
  avatarUrl?: string | null;
}

export interface EpisodeDotInfo {
  episodeNumber: number;
  characterName: string;
  avatarUrl: string | null;
}

export interface EpisodeSummary {
  title: string;
  narrativeRecap: string;
  keyMoments: string[];
  mentionedCharacters: string[];
}

export interface NPCReaction {
  relationshipScore: number;
  willHelpInFuture: 'yes' | 'no' | 'depends';
  dimensions: {
    trust: { score: number; reason: string };
    respect: { score: number; reason: string };
    interest: { score: number; reason: string };
    suspicion: { score: number; reason: string };
  };
  learnedFacts: string;
  gossip: string;
  narrativeHooks: string[];
}

export interface ScenarioData {
  title: string;
  description: string;
  situation: string;
  /** Absent on episode 1 (the conjuntura now lives in the opening storyboard's page 3). Present on episode 2+ bridges. */
  frontendSituation?: string | null;
  characterName?: string;
  aiRole: string;
  userRole: string;
  characterDescription?: string;
  playerFacingDescription?: string;
  avatarImages?: Record<string, string>;
  /** Small (~400px) JPEG preview of the neutral avatar, for progress dots and the "Vais falar com" card. Falls back server-side to avatarImages.neutral when not yet backfilled. */
  avatarThumbUrl?: string | null;
  /** AI-selected voice matching character gender/personality */
  voice?: string;
  /** Pre-built prompt from backend (via PromptService, same pipeline as soft skills) */
  prompt?: string;
  /** Investigation notebook: extracted facts from previous episodes, injected by /play endpoint */
  userReferenceInfo?: string;
}

export interface NextEpisodeCandidate {
  id: string;
  name: string;
  role: string;
  hook: string;
  reasonSafe: string;
  referenceSource: string;
  isKnownCharacter: boolean;
  genderHint?: 'male' | 'female' | 'unknown';
  avatarUrl?: string | null;
  interactionCount: number;
  interactionLimit: number;
  /** Modo exploração: perguntas abertas dos leads de factos já entregues que esta pessoa pode responder. */
  openQuestions?: string[];
}

interface SelectNextCandidateResponse {
  status: 'selected';
}

interface ResolveCaseResponse {
  status: 'resolve_started';
}

interface RestartAdventureResponse {
  status: 'restarted';
  magicLinkUrl: string;
  token: string;
  resetCount: number;
}

class GameApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * Arranque anónimo: cria um playerState sem email e devolve o token JWT
   * (365 dias) para o device guardar em cookie/localStorage. O jogador
   * começa a jogar imediatamente — o email só é pedido no fim do episódio 2
   * (attachEmail). Sem rate-limit de email (não envia email nenhum).
   */
  async startAnonymous(adventureId?: string): Promise<StartAnonymousResponse> {
    const response = await fetch(`${this.baseUrl}/api/game/start-anonymous`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...(adventureId ? { adventureId } : {}) }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || data.error || 'Failed to start anonymous game');
    }

    return data;
  }

  /**
   * Banner server-backed: valida/renova o token do cookie e devolve o
   * progresso actual do run. Usado pelo catálogo e pela página de detalhe
   * no mount (F3). Se o servidor renovar o token (estava expirado), o
   * cliente regrava o cookie.
   */
  async resume(token: string): Promise<ResumeResponse> {
    const response = await fetch(`${this.baseUrl}/api/game/resume/${encodeURIComponent(token)}`);

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || data.error || 'Failed to resume session');
    }

    return data;
  }

  /**
   * Anexar email a um playerState anónimo já existente (token do device).
   * Pede-se o email no fim do episódio 2 (F4) para sincronização
   * cross-device. Se o email já tiver um run noutro device, devolve
   * `conflict` com os dois resumos — o cliente abre o modal de escolha (F5).
   */
  async attachEmail(token: string, email: string): Promise<AttachEmailResponse> {
    const response = await fetch(`${this.baseUrl}/api/game/attach-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, email }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || data.error || 'Failed to attach email');
    }

    return data;
  }

  /**
   * Resolver o conflito quando attachEmail devolveu `conflict`. O jogador
   * escolhe qual run manter:
   *  - keep:'current'  → o run anónimo deste device vence (devolve `attached`).
   *  - keep:'existing' → mantém-se o run do email (devolve `switch` com
   *    token novo para o cliente navegar para o run existente).
   */
  async resolveEmailConflict(
    token: string,
    email: string,
    keep: 'current' | 'existing',
  ): Promise<ResolveEmailConflictResponse> {
    const response = await fetch(`${this.baseUrl}/api/game/attach-email/resolve-conflict`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, email, keep }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || data.error || 'Failed to resolve email conflict');
    }

    return data;
  }

  /**
   * List active adventures available in the public catalog.
   * Powers the catalog page (`/`).
   */
  async listAdventures(): Promise<AdventuresListResponse> {
    const response = await fetch(`${this.baseUrl}/api/game/adventures`);

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || data.error || 'Failed to list adventures');
    }

    return data;
  }

  /**
   * Fetch the full public summary for a single adventure.
   * Powers the detail page (`/adventure/:id`).
   */
  async getAdventure(id: string): Promise<AdventureDetailResponse> {
    const response = await fetch(`${this.baseUrl}/api/game/adventures/${encodeURIComponent(id)}`);

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || data.error || 'Failed to get adventure');
    }

    return data;
  }

  /**
   * Fetch the full public summary for a single adventure by URL slug.
   * Powers the detail page (`/adventure/:slug`). Falls back to treating the
   * value as a legacy document id on the backend.
   */
  async getAdventureBySlug(slug: string): Promise<AdventureDetailResponse> {
    const response = await fetch(
      `${this.baseUrl}/api/game/adventures/slug/${encodeURIComponent(slug)}`,
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || data.error || 'Failed to get adventure');
    }

    return data;
  }

  /**
   * Build the URL of the dynamically-generated Open Graph preview image for
   * an adventure (cover + title + Selna logo). Used in `og:image` /
   * `twitter:image` meta tags on the detail page.
   */
  adventureOgImageUrl(adventureId: string): string {
    return `${this.baseUrl}/api/game/adventures/${encodeURIComponent(adventureId)}/og-image`;
  }

  /**
   * Start or continue a game by email.
   *
   * @param email The player's email.
   * @param adventureId Optional adventure id picked from the catalog. When
   *   provided, the backend upserts an `adventureAssignments` row so the
   *   upcoming magic link resolves to the chosen adventure. If the email
   *   already has a player state for a different adventure, the backend
   *   archives the old playthrough and starts the new one.
   */
  async startGame(email: string, adventureId?: string): Promise<StartGameResponse> {
    const response = await fetch(`${this.baseUrl}/api/game/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, ...(adventureId ? { adventureId } : {}) }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || data.error || 'Failed to start game');
    }

    // API uses sendSuccess which spreads data directly (not nested under .data)
    return data;
  }


  /**
   * Get episode data using magic link token
   */
  async getEpisodeData(token: string): Promise<PlayResponse> {
    const response = await fetch(`${this.baseUrl}/api/game/play/${token}`);

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || data.error || 'Failed to load episode');
    }

    return data;
  }

  /**
   * Select the next NPC to interview.
   */
  async selectNextCandidate(token: string, candidateId: string): Promise<SelectNextCandidateResponse> {
    const response = await fetch(`${this.baseUrl}/api/game/choose-next`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, candidateId }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || data.error || 'Failed to choose next character');
    }

    return data;
  }

  /**
   * Complete an episode
   */
  async completeEpisode(
    token: string,
    transcript: string,
    sessionData: { duration: number; turnCount: number }
  ): Promise<EpisodeCompleteResponse> {
    const response = await fetch(`${this.baseUrl}/api/game/episode-complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, transcript, sessionData }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || data.error || 'Failed to complete episode');
    }

    return data;
  }

  /**
   * Get waiting status and recap
   */
  async getWaitingStatus(token: string): Promise<WaitingResponse> {
    const response = await fetch(`${this.baseUrl}/api/game/waiting/${token}`);

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || data.error || 'Failed to get status');
    }

    return data;
  }

  /**
   * Resolve the case early and trigger final evaluation.
   */
  async resolveCase(token: string): Promise<ResolveCaseResponse> {
    const response = await fetch(`${this.baseUrl}/api/game/resolve-case`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || data.error || 'Failed to resolve case');
    }

    return data;
  }

  /**
   * Get completion data for a finished adventure
   */
  async getCompletionData(token: string): Promise<CompletionResponse> {
    const response = await fetch(`${this.baseUrl}/api/game/completion/${token}`);

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || data.error || 'Failed to get completion data');
    }

    return data;
  }

  /**
   * Submit highscore entry
   */
  async submitHighscore(token: string, playerName: string): Promise<HighscoreSubmitResponse> {
    const response = await fetch(`${this.baseUrl}/api/game/highscore`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, playerName }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || data.error || 'Failed to submit highscore');
    }

    return data;
  }

  /**
   * Get highscores leaderboard
   */
  /**
   * Restart a completed adventure from episode 1
   */
  async restartAdventure(token: string): Promise<RestartAdventureResponse> {
    const response = await fetch(`${this.baseUrl}/api/game/restart`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || data.error || 'Failed to restart adventure');
    }

    return data;
  }

  /**
   * Save mid-episode state for later resumption
   */
  async saveEpisodeState(token: string, saveState: EpisodeSaveState): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/game/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, saveState }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error?.message || data.error || 'Failed to save episode state');
    }
  }

  /**
   * Clear saved episode state (when starting fresh)
   */
  async clearSavedEpisodeState(token: string): Promise<void> {
    // Clearing is done by saving an empty state, which the backend handles
    // by deleting the field. We reuse the save endpoint with a special flag.
    const response = await fetch(`${this.baseUrl}/api/game/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, clearSave: true }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error?.message || data.error || 'Failed to clear saved state');
    }
  }

  async getHighscores(adventureId?: string, limit?: number): Promise<HighscoresListResponse> {
    const params = new URLSearchParams();
    if (adventureId) params.append('adventureId', adventureId);
    if (limit) params.append('limit', limit.toString());

    const url = `${this.baseUrl}/api/game/highscores${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url);

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || data.error || 'Failed to get highscores');
    }

    return data;
  }
}

export const gameApi = new GameApiService();
export default gameApi;
